import * as R from 'ramda';
import * as React from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Container, Form, FormGroup, FormText, Input, Label } from 'reactstrap';
import { withBorder } from '../../../reactutils/withBorder';
import { withLogin } from '../../../reactutils/withLogin';
import { withNavLocation } from '../../../reactutils/withNavLocation';
import { withTitle } from '../../../reactutils/withTitle';
import { UserProfileSettings } from '../../../services/account';
import { TournamentSubscription } from '../../../services/betting';
import { isPushNotificationSupported } from '../../../utils';
import { LoadingIndicatorSpinner } from '../../common/LoadingIndicatorSpinner';
import { Section, SectionHeading } from '../../common/Section';
import { systemPushSubscriptionSubscribe, systemPushSubscriptionUnsubscribe } from '../../System/actions';
import { isSysAdminSelector, systemPushSubscriptionSelector, userNameSelector } from '../../System/selectors';
import { getPushNotificationApi } from '../../System/systemConfig';
import { EmailConfirmationTeaser } from '../EmailConfirmationTeaser';
import { InvalidEmailTeaser } from '../InvalidEmailTeaser';
import { NewsletterTeaser } from '../NewsletterTeaser';
import { ProfileSubMenu } from '../ProfileSubMenu';
import {
    accountUserSettingLoadSubscribedTournaments,
    accountUserSettingLoadUserSettings,
    accountUserSettingsSaveSettings,
} from './actions';
import { ChangeEmail } from './ChangeEmail';
import ChangePassword from './ChangePassword';
import DeleteProfile from './DeleteProfile';
import {
    accountUserSettingsProfileSettingsSelector,
    accountUserSettingsSubscribedTournamentsSelector,
} from './selectors';

const TOURNAMENT_PREFIX = 'T_';

const AccountUserSettingsComponent: React.FC<{}> = () => {
    const subscribedTournaments = useSelector(accountUserSettingsSubscribedTournamentsSelector);
    const userProfileSettings = useSelector(accountUserSettingsProfileSettingsSelector);

    const dispatch = useDispatch();

    const [tournaments, setTournaments] = React.useState<TournamentSubscription[]>([]);
    const [profile, setProfile] = React.useState<UserProfileSettings>(userProfileSettings);

    const pushNotificationsSupported = isPushNotificationSupported();

    const pushSubscription = useSelector(systemPushSubscriptionSelector);
    const isSysAdmin = useSelector(isSysAdminSelector);

    const [testTitle, setTestTitle] = React.useState('Bayern - Dortmund 1:1');
    const [testBody, setTestBody] = React.useState('1. Bundesliga 2022/23, 17. Spieltag\nSpielbeginn: 15:30 Uhr');
    const [testIcon, setTestIcon] = React.useState('/images/soccer4.png');

    interface TestMessage {
        title: string;
        body: string;
        icon: string;
    }

    const api = getPushNotificationApi();
    const testMessageMutation = useMutation((v: TestMessage) => api.test(v.title, v.body, v.icon));

    const onLoad = React.useCallback(() => {
        dispatch(accountUserSettingLoadSubscribedTournaments());
        dispatch(accountUserSettingLoadUserSettings());
    }, [dispatch]);

    const onSave = () => {
        dispatch(accountUserSettingsSaveSettings({ subscribedTournaments: tournaments, userProfileSettings: profile }));
    };

    React.useEffect(() => {
        onLoad();
    }, [onLoad]);

    React.useEffect(() => {
        setTournaments(subscribedTournaments);
        setProfile(userProfileSettings);
    }, [subscribedTournaments, userProfileSettings]);

    const onChange = (ev: React.FormEvent<HTMLInputElement>) => {
        if (ev.currentTarget.type === 'checkbox') {
            if (ev.currentTarget.name.startsWith(TOURNAMENT_PREFIX)) {
                // we need to update the tournament subscription array
                const tournamentId = Number(ev.currentTarget.name.substring(TOURNAMENT_PREFIX.length));
                const tournaments = subscribedTournaments.slice();
                const selectedTS = tournaments.find((ts) => ts.tournamentId === tournamentId);
                if (selectedTS) {
                    selectedTS.subscribed = ev.currentTarget.checked;
                    setTournaments(tournaments);
                }
            } else {
                setProfile({ ...profile, [ev.currentTarget.name]: ev.currentTarget.checked });
            }
        } else {
            // we currently don't have other values
        }
    };

    const pushSubscriptionChange = (ev: React.FormEvent<HTMLInputElement>) => {
        if (ev.currentTarget.type === 'checkbox') {
            if (ev.currentTarget.checked) {
                dispatch(systemPushSubscriptionSubscribe());
            } else {
                dispatch(systemPushSubscriptionUnsubscribe());
            }
        }
    };

    const sendTestMessage = () => {
        testMessageMutation.mutate({ title: testTitle, body: testBody, icon: testIcon });
    };

    return (
        <>
            <ProfileSubMenu subMenu="settings" />
            <EmailConfirmationTeaser />
            <NewsletterTeaser />
            <InvalidEmailTeaser />
            <Section>
                <SectionHeading>Push-Benachrichtigungen</SectionHeading>
                <FormGroup check={true}>
                    <Label>
                        <Input
                            type="checkbox"
                            name="pushNotifications"
                            checked={pushSubscription !== undefined}
                            onChange={pushSubscriptionChange}
                            disabled={!profile.infoMails || !pushNotificationsSupported}
                        />
                        Ich möchte Push-Benachrichtigungen auf diesem Gerät erhalten.
                        {pushSubscription !== undefined && profile.infoMails && isSysAdmin && (
                            <Form className="p-3 bg-danger">
                                <FormGroup>
                                    <Label className="text-white" size="sm">
                                        Titel
                                    </Label>
                                    <Input
                                        bsSize="sm"
                                        type="text"
                                        placeholder="Titel"
                                        value={testTitle}
                                        onChange={(e) => {
                                            setTestTitle(e.currentTarget.value);
                                        }}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label className="text-white" size="sm">
                                        Body
                                    </Label>
                                    <Input
                                        bsSize="sm"
                                        type="textarea"
                                        placeholder="Body"
                                        value={testBody}
                                        onChange={(e) => {
                                            setTestBody(e.currentTarget.value);
                                        }}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label className="text-white" size="sm">
                                        Icon
                                    </Label>
                                    <Input
                                        bsSize="sm"
                                        type="text"
                                        placeholder="Icon"
                                        value={testIcon}
                                        onChange={(e) => {
                                            setTestIcon(e.currentTarget.value);
                                        }}
                                    />
                                </FormGroup>
                                <Button size="sm" onClick={sendTestMessage}>
                                    Test
                                </Button>
                            </Form>
                        )}
                    </Label>

                    <FormText>
                        <br/>
                        {pushNotificationsSupported && <span>Push-Nachrichten werden vom Gerät unterstützt.</span>}
                        {!pushNotificationsSupported && (
                            <span>Push-Nachrichten werden vom Gerät nicht unterstützt.</span>
                        )}
                    </FormText>
                </FormGroup>
            </Section>

            <Section>
                <SectionHeading>Email-Benachrichtigungen</SectionHeading>
                {profile && (
                    <div>
                        <FormGroup check={true}>
                            <Label check={true}>
                                <Input
                                    type="checkbox"
                                    name="infoMails"
                                    checked={profile.infoMails}
                                    onChange={onChange}
                                />{' '}
                                Ich möchte grundsätzlich Email-Nachrichten bekommen.
                            </Label>
                        </FormGroup>
                        <Container>
                            <FormGroup check={true}>
                                <Label>
                                    <Input
                                        type="checkbox"
                                        name="newsletter"
                                        checked={profile.newsletter}
                                        onChange={onChange}
                                        disabled={!profile.infoMails}
                                    />
                                    Ich möchte den Newsletter bekommen.
                                </Label>
                            </FormGroup>
                        </Container>
                    </div>
                )}
                {!profile && <LoadingIndicatorSpinner />}

                {tournaments && profile && (
                    <FormGroup tag="fieldset" disabled={!profile.infoMails}>
                        <b>Schicken Sie mir Tipperinnerungen für die folgenden Turniere:</b>
                        {tournaments &&
                            tournaments.map((ts, index) => (
                                <FormGroup check={true} key={ts.tournamentId}>
                                    <Label>
                                        <Input
                                            type="checkbox"
                                            name={TOURNAMENT_PREFIX + ts.tournamentId}
                                            checked={ts.subscribed}
                                            onChange={onChange}
                                            disabled={!profile.infoMails}
                                        />
                                        {ts.tournamentName}
                                    </Label>
                                </FormGroup>
                            ))}
                    </FormGroup>
                )}
                {!(tournaments && profile) && <LoadingIndicatorSpinner />}
                <Button onClick={onSave}>Speichern</Button>
            </Section>
            <ChangePassword />
            <ChangeEmail />
            <DeleteProfile />
        </>
    );
};

export const AccountUserSettingsScreen = R.compose(
    withBorder(),
    withTitle('Einstellungen', userNameSelector),
    withNavLocation('profile'),
    withLogin()
)(AccountUserSettingsComponent);
